import React, { useEffect, useMemo, useState } from "react";
import "./batAI.css";

const MAX_QUERY_LENGTH = 1000;

function renderInlineMarkdown(text) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>;
    }

    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
}

function BatAIAnswer({ answer }) {
  const lines = useMemo(() => String(answer || "").split(/\r?\n/), [answer]);

  return (
    <div className="bat-ai-answer-copy">
      {lines.map((line, index) => {
        const trimmed = line.trim();

        if (!trimmed) {
          return <div className="bat-ai-answer-gap" key={`gap-${index}`} />;
        }

        const bulletMatch = trimmed.match(/^(?:[-*•])\s+(.*)$/);
        if (bulletMatch) {
          return (
            <div className="bat-ai-answer-bullet" key={`bullet-${index}`}>
              <span className="bat-ai-answer-bullet-mark">•</span>
              <span>{renderInlineMarkdown(bulletMatch[1])}</span>
            </div>
          );
        }

        return (
          <p key={`line-${index}`}>
            {renderInlineMarkdown(trimmed)}
          </p>
        );
      })}
    </div>
  );
}

export default function BatAI() {
  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState("");
  const [sources, setSources] = useState([]);
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;

    const timer = window.setInterval(() => {
      setCooldownSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldownSeconds]);

  const trimmedQuery = query.trim();
  const canSubmit =
    !loading &&
    cooldownSeconds === 0 &&
    trimmedQuery.length >= 2 &&
    trimmedQuery.length <= MAX_QUERY_LENGTH;

  const submitSearch = async (event) => {
    event.preventDefault();

    const outgoingQuery = query.split("\u0000").join("").trim();

    if (outgoingQuery.length < 2) {
      setError("Ask Bat AI a question using at least 2 characters.");
      return;
    }

    if (outgoingQuery.length > MAX_QUERY_LENGTH) {
      setError(`Keep your question under ${MAX_QUERY_LENGTH} characters.`);
      return;
    }

    setLoading(true);
    setError("");
    setAnswer("");
    setSources([]);
    setSubmittedQuery(outgoingQuery);

    try {
      const response = await fetch("/api/ai/search", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: outgoingQuery }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Your Pogforts session has expired. Log in again to use Bat AI.");
        }

        if (response.status === 429) {
          const retryAfterMs = Number(data.retryAfterMs || 0);
          const retryAfterHeader = Number(response.headers.get("Retry-After") || 0);
          const seconds = Math.max(
            1,
            retryAfterHeader || Math.ceil(retryAfterMs / 1000) || 1
          );
          setCooldownSeconds(seconds);
        }

        throw new Error(data.error || "Bat AI is temporarily unavailable.");
      }

      setAnswer(String(data.answer || ""));
      setSources(Array.isArray(data.sources) ? data.sources : []);
    } catch (requestError) {
      console.error("Bat AI web search failed:", requestError);
      setError(requestError.message || "Bat AI is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bat-ai-page" aria-labelledby="bat-ai-title">
      <div className="bat-ai-shell">
        <header className="bat-ai-header">
          <div className="bat-ai-kicker">POGFORTS INTELLIGENCE</div>
          <h1 id="bat-ai-title">BAT AI</h1>
          <p>
            Search Pogforts knowledge, Magical Sorting Bat commands, Murder Mystery
            Magic and the wider Pogforts universe.
          </p>
        </header>

        <form className="bat-ai-search-form" onSubmit={submitSearch}>
          <label htmlFor="bat-ai-query">Ask Bat AI</label>
          <textarea
            id="bat-ai-query"
            value={query}
            onChange={(event) => setQuery(event.target.value.slice(0, MAX_QUERY_LENGTH))}
            placeholder="What are the four Pogforts houses?"
            maxLength={MAX_QUERY_LENGTH}
            rows={4}
            disabled={loading}
          />

          <div className="bat-ai-search-footer">
            <span className="bat-ai-counter">
              {query.length}/{MAX_QUERY_LENGTH}
            </span>

            <button type="submit" className="bat-ai-search-button" disabled={!canSubmit}>
              {loading
                ? "SEARCHING..."
                : cooldownSeconds > 0
                  ? `WAIT ${cooldownSeconds}s`
                  : "SEARCH"}
            </button>
          </div>
        </form>

        {error && (
          <div className="bat-ai-status bat-ai-status-error" role="alert">
            {error}
          </div>
        )}

        {loading && (
          <div className="bat-ai-status" role="status">
            <span className="bat-ai-pulse" aria-hidden="true" />
            Bat AI is searching Pogforts knowledge...
          </div>
        )}

        {!loading && answer && (
          <article className="bat-ai-result" aria-live="polite">
            <div className="bat-ai-result-heading">
              <span>BAT AI</span>
              {submittedQuery && <small>{submittedQuery}</small>}
            </div>

            <BatAIAnswer answer={answer} />

            {sources.length > 0 && (
              <div className="bat-ai-sources">
                <div className="bat-ai-sources-title">
                  {sources.length === 1 ? "SOURCE" : "SOURCES"}
                </div>
                <div className="bat-ai-source-list">
                  {sources.map((source, index) => (
                    <span className="bat-ai-source-chip" key={`${source.title}-${index}`}>
                      {source.title || "Pogforts Knowledge Base"}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </article>
        )}
      </div>
    </section>
  );
}
